// Reports.tsx
import useFetch from "../../../hooks/useFetch";
import { useContext, useEffect, useRef, useState } from "react";
import {
  Button,
  Drawer,
  Input,
  InputRef,
  Space,
  Table,
  TablePaginationConfig,
  Tooltip,
} from "antd";
import { PageHeader } from "../../../components/page-header/page-header";
import useToast from "../../../hooks/useToast";
import { TContext, TFilterModel } from "../../../types/config";
import {
  eREPORTTYPE,
  eResultCode,
  reportTypeMapping,
  eDATE_FORMAT,
} from "../../../utils/enum";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import { defaultFilterParams } from "../../../utils/constants";
import { GetReportLogsList } from "../../../utils/api.constant";
import utils from "../../../utils";
import { TReport } from "../../../types";
import DateUtils from "../../../utils/date";
import ColumnManager from "../../../components/configurable-columns";
import {
  FilterFilled,
  FilterOutlined,
  OrderedListOutlined,
  SearchOutlined,
  CloseCircleFilled,
} from "@ant-design/icons";
import ExportToCSV from "../../../components/exporttocsv/ExportToCsv";
import Highlighter from "react-highlight-words";
import { FilterDropdownProps } from "antd/es/table/interface";
import CustomDrawer from "../../../components/drawer";
import ReportForm from "./form";
import { UserContext } from "../../../context";

export default function ReportsList() {
  // initialization of hooks
  const { post } = useFetch(); // custom hook used to call the apis
  const [isLoading, setIsLoading] = useState(true); // to manage the loader
  const [data, setData] = useState<TReport[]>([]); // to store config group data
  const [filterParams, setFilterParams] =
    useState<TFilterModel>(defaultFilterParams); // object to fetch the data in list
  const { onShowToast } = useToast(); // custom hook used to show toast
  const searchInput = useRef<InputRef>(null);
  const [globalsearchText, setGlobalSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const context: TContext = useContext(UserContext);
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});

  const handleReset = (clearFilters: FilterDropdownProps["clearFilters"]) => {
    if (clearFilters) {
      clearFilters();
    }
    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex: any): any => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }: any) => (
      <div style={{ padding: 8 }} onKeyDown={(e: any) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder="Filter by keyword"
          prefix={
            <SearchOutlined
              style={{ color: "rgba(0,0,0,.45)", marginRight: 4 }}
            />
          } // Adds and spaces the search icon
          value={selectedKeys[0]}
          onChange={(e: any) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{
            marginBottom: 8,
            display: "flex",
            paddingLeft: "12px", // Ensure spacing for both icon and text
          }}
        />
        <Space style={{ display: "flex", justifyContent: "space-between" }}>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            type="link"
            size="small"
            style={{ width: 45 }}
          >
            Reset
          </Button>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            size="small"
            style={{ width: 50 }}
          >
            Ok
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <FilterFilled
        style={{ color: filtered ? "var(--bs-primary)" : undefined }}
      />
    ),
    onFilter: (value: any, record: any) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    render: (text: any) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "var(--bs-primary)",
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps["confirm"],
    dataIndex: any,
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const initialColumns: any[] = [
    {
      title: "Report Type",
      dataIndex: "reportType",
      key: "reportType",
      // width: "25%",
      filterSearch: true,
      sorter: (a: TReport, b: TReport) =>
        String(a.reportType).localeCompare(String(b.reportType)),
      render: (reportType: number) => {
        return reportTypeMapping[reportType as eREPORTTYPE] || reportType;
      },
      filters: Object.keys(reportTypeMapping).map((key) => ({
        text: reportTypeMapping[key as unknown as eREPORTTYPE],
        value: key,
      })),
      onFilter: (value: string | number | boolean, record: TReport) =>
        record.reportType === Number(value),
    },
    {
      title: "Customer",
      dataIndex: "customerName",
      key: "customerName",
      // width: "30%",
      filterSearch: true,
      sorter: (a: TReport, b: TReport) =>
        String(a.customerName).localeCompare(String(b.customerName)),
      filters: utils.getUniqueFilters(data, "customerName"),
      onFilter: (value: string | number | boolean, record: TReport) =>
        record.customerName.includes(value as string),
    },
    {
      title: "Downloaded On",
      dataIndex: "createdOn",
      key: "createdOn",
      // width: "15%",
      filterSearch: true,
      sorter: (a: TReport, b: TReport) =>
        String(a.createdOn).localeCompare(String(b.createdOn)),
      render: (createdOn: string) => {
        return DateUtils.format(createdOn, context?.user?.dateFormat);
      },
    },
    {
      title: "Downloaded By",
      dataIndex: "createdByName",
      key: "createdByName",
      ...getColumnSearchProps("createdByName"),
      // width: "30%",
      filterSearch: true,
      sorter: (a: TReport, b: TReport) =>
        a?.createdByName?.localeCompare(b?.createdByName),
      onFilter: (value: string | number | boolean, record: TReport) =>
        record?.createdByName?.includes(value as string),
    },
  ];
  const [columns, setColumns] = useState(initialColumns);
  const [searchText, setSearchText] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [draftColumns, setDraftColumns] = useState(columns);

  useEffect(() => {
    fetchData();
  }, []);

  const filteredData = data.filter((item) => {
    const searchMatch = Object.values(item).some((val) =>
      val?.toString().toLowerCase().includes(globalsearchText.toLowerCase()),
    );
    const reportTypeMatch =
      !activeFilters.reportType ||
      activeFilters.reportType.includes(String(item.reportType));

    const customerNameMatch =
      !activeFilters.customerName ||
      activeFilters.customerName.includes(item.customerName);

    return searchMatch && reportTypeMatch && customerNameMatch;
  });

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: any,
  ) => {
    setActiveFilters(filters);
    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if ("dataIndex" in col) {
          return {
            ...col,
            filteredValue: filters[col.dataIndex as string] || null,
            sortOrder: col.dataIndex === sorter.field ? sorter.order : null,
          };
        }
        return col;
      }),
    );
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const requestPayload = {
        data: {
          currentPage: 1,
          pageSize: -1,
        },
      };

      const response = await post(GetReportLogsList, requestPayload);
      const { data, dataResponse, filterModel } = response;
      const { returnCode, description } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        const formattedData = data.map((item: TReport, index: number) => ({
          key: (item.id ?? "").toString(),
          srNo: index + 1,
          ...item,
        }));
        setData(formattedData);
        setFilterParams({
          ...(filterModel as TFilterModel),
          totalRows: data.length,
        });
        setIsLoading(false);
        updateColumnFilters(filteredData);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: description,
        });
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateColumnFilters = (dataset: TReport[]) => {
    const reportTypeFilters = Object.entries(reportTypeMapping)
      .filter(([key, value]) => {
        return dataset.some((item) => item.reportType === Number(key));
      })
      .map(([key, value]) => ({
        text: value,
        value: key,
      }));
    const customerNameFilters = Array.from(
      new Set(dataset.map((item) => item.customerName)),
    ).map((name) => ({ text: name, value: name }));

    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if (col.key === "reportType") {
          return { ...col, filters: reportTypeFilters };
        }
        if (col.key === "customerName") {
          return { ...col, filters: customerNameFilters };
        }
        return col;
      }),
    );
  };

  useEffect(() => {
    updateColumnFilters(filteredData);
  }, [activeFilters, data]);

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    setDraftColumns((prevDraftColumns) => {
      const reorderedColumns = Array.from(prevDraftColumns);
      const [removed] = reorderedColumns.splice(result.source.index, 1);
      reorderedColumns.splice(result.destination.index, 0, removed);
      return reorderedColumns;
    });
  };

  const toggleColumn = (key: string) => {
    setDraftColumns((prevDraftColumns) =>
      prevDraftColumns.map((col) =>
        col.key === key ? { ...col, hidden: !col.hidden } : col,
      ),
    );
  };

  const handleResetColumns = () => {
    setDraftColumns(initialColumns); // Reset staging state to initial state
  };

  const handleSaveColumns = () => {
    setColumns(draftColumns); // Apply changes to the final state
    setDrawerVisible(false); // Close the drawer
  };

  const visibleColumns = columns.filter((col) => !col.hidden);

  const mapToExportRow = (item: TReport) => {
    const { reportType, createdOn, ...restData } = item;

    const formatExportDate = (dateVal: any, showTime: boolean = false) => {
      return DateUtils.formatForExport(
        dateVal,
        showTime,
        context?.user?.dateFormat as eDATE_FORMAT,
      );
    };

    return {
      reportType:
        reportTypeMapping[item.reportType as eREPORTTYPE] || item.reportType,
      createdOn: formatExportDate(createdOn, true),
      ...restData,
    };
  };

  const exportData = data.map(mapToExportRow);
  const exportFilteredData = filteredData.map(mapToExportRow);

  const handleResetAll = () => {
    setActiveFilters({});
    setSearchText("");
    setColumns((prevColumns) =>
      prevColumns.map((col) => ({
        ...col,
        filteredValue: null,
        sortOrder: null,
      })),
    );
    // setTableKey((prev) => prev + 1);
    // fetchData(1, 10); // Fetch data with default pagination
  };

  return (
    <div className="pageBody">
      <PageHeader
        // icon={<FaObjectGroup />}
        title={`${filterParams?.totalRows} Total Downloaded Reports`}
        size="large"
      >
        <Space className="headerItems" wrap>
          {/* {Utils.isUserHasAccess(
            context?.user?.privilegeList,
            ePrivileges.DOWNLOADSLAREPORT
          ) ||
          Utils.isUserHasAccess(
            context?.user?.privilegeList,
            ePrivileges.DOWNLOADINSTALLATIONREPORT
          ) ||
          Utils.isUserHasAccess(
            context?.user?.privilegeList,
            ePrivileges.DOWNLOADSERVICEREPORT
          ) ||
          Utils.isUserHasAccess(
            context?.user?.privilegeList,
            ePrivileges.DOWNLOADYEARLYDEALERREPORT
          ) ||
          Utils.isUserHasAccess(
            context?.user?.privilegeList,
            ePrivileges.DOWNLOADDEALERACTIVEASSETREPORT
          ) ||
          Utils.isUserHasAccess(
            context?.user?.privilegeList,
            ePrivileges.DOWNLOADINSTALLCOSTREPORT
          ) ? ( */}
          <CustomDrawer
            trigger={
              <Button disabled={isLoading} type="primary">
                Download
              </Button>
            }
            componentProps={{
              onRefreshList: () => fetchData(),
            }}
            component={ReportForm} // Passing DealerForm component
            title="Reports"
            size="default"
          />
          {/* ) : null} */}

          {/* MOVED TO REPORT LIST */}
          {/* <CustomDrawer
            trigger={
              <Button disabled={isLoading} type="primary">
                Schedule
              </Button>
            }
            component={ScheduleReportForm} // Passing DealerForm component
            title="Schedule Report"
            size="default"
          /> */}
          <Input
            placeholder="Filter by keywords"
            value={globalsearchText}
            onChange={(e) => setGlobalSearchText(e.target.value)}
            style={{ width: 200, borderColor: "var(--bs-primary-light)" }}
            prefix={<SearchOutlined />}
          />
          <ExportToCSV
            data={exportData}
            filteredData={exportFilteredData}
            allColumns={initialColumns}
            visibleColumns={visibleColumns}
            moduleName="Report List"
          />
          <Tooltip title="Reset Filters">
            <FilterOutlined onClick={handleResetAll} />
          </Tooltip>
          <Tooltip title="Configure Columns">
            <OrderedListOutlined onClick={() => setDrawerVisible(true)} />
          </Tooltip>
        </Space>
      </PageHeader>
      <div className="tableWrap">
        <Table
          columns={visibleColumns}
          dataSource={filteredData}
          loading={isLoading}
          pagination={{
            // ...pagination,
            position: ["bottomLeft"],
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total: any, range: any[]) =>
              `Showing ${range[0]}-${range[1]} of ${total} total entries`,
          }}
          onChange={handleTableChange}
          bordered={false}
          size="small"
          // sticky
          scroll={{
            x: "max-content", // Adjust height dynamically
          }}
        />
      </div>
      <Drawer
        title="Customize Columns"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        style={{ transition: "transform 0.3s ease-in-out" }}
      >
        <ColumnManager
          columns={draftColumns}
          initialColumns={initialColumns}
          onDragEnd={handleDragEnd}
          onToggleColumn={toggleColumn}
          onResetColumns={handleResetColumns}
          onSave={handleSaveColumns}
          onCancel={() => setDrawerVisible(false)}
        />
      </Drawer>
    </div>
  );
}
